// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { ReactNode } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { Text } from '@/components/Themed';
import appColors from '@/theme/colors';

/** A titled panel. Every block on both statistics views is one of these. */
export function StatCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <View style={styles.card}>
      <Text style={styles.cardTitle}>{title}</Text>
      {children}
    </View>
  );
}

/**
 * A headline number with its label. `delta` is the period-over-period change and is only
 * rendered when there is one — an absent comparison reads better as nothing than as "—".
 */
export function StatTile({
  value,
  suffix,
  label,
  delta,
  deltaTone = 'neutral',
  gold = false,
  testID,
}: {
  value: string;
  suffix?: string;
  label: string;
  delta?: string | null;
  deltaTone?: 'good' | 'bad' | 'neutral';
  gold?: boolean;
  testID?: string;
}) {
  return (
    <View style={styles.tile} testID={testID}>
      <Text style={[styles.tileValue, gold && styles.tileValueGold]}>
        {value}
        {suffix ? <Text style={styles.tileSuffix}> {suffix}</Text> : null}
      </Text>
      <Text style={styles.tileLabel}>{label}</Text>
      {delta ? (
        <Text
          style={[
            styles.tileDelta,
            deltaTone === 'bad' && styles.tileDeltaBad,
            deltaTone === 'neutral' && styles.tileDeltaNeutral,
          ]}
        >
          {delta}
        </Text>
      ) : null}
    </View>
  );
}

export function StatTileRow({ children }: { children: ReactNode }) {
  return <View style={styles.tileRow}>{children}</View>;
}

/**
 * A labelled bar with its count. The number is always spelled out next to the bar, so length is
 * reinforcement, not the only encoding.
 *
 * `inline` puts label, bar and value on one line — right for the score distribution, whose five
 * labels are short and fixed. `stacked` gives the label its own line above the bar, which is what
 * game names need: they run to "Best ball - worst ball", and no fixed label column fits that on a
 * phone without truncating it to nothing.
 */
export function StatBarRow({
  label,
  fraction,
  color,
  value,
  layout = 'inline',
  onPress,
  testID,
}: {
  label: string;
  /** 0..1 of the row's available width. */
  fraction: number;
  color: string;
  value: string;
  layout?: 'inline' | 'stacked';
  onPress?: () => void;
  testID?: string;
}) {
  const width = `${Math.max(2, Math.min(100, fraction * 100))}%` as const;
  const bar = (
    <View style={styles.barTrack}>
      <View style={[styles.barFill, { width, backgroundColor: color }]} />
    </View>
  );

  const content =
    layout === 'stacked' ? (
      <View style={styles.stackedRow} testID={testID}>
        <View style={styles.stackedHeader}>
          <Text style={styles.stackedLabel} numberOfLines={1}>
            {label}
          </Text>
          <Text style={styles.stackedValue} numberOfLines={1}>
            {value}
          </Text>
        </View>
        {bar}
      </View>
    ) : (
      <View style={styles.barRow} testID={testID}>
        <Text style={styles.barLabel} numberOfLines={1}>
          {label}
        </Text>
        {bar}
        <Text style={styles.barValue} numberOfLines={1}>
          {value}
        </Text>
      </View>
    );

  return onPress ? <Pressable onPress={onPress}>{content}</Pressable> : content;
}

/** A key/value line — the records list and anything else that is two bits of text. */
export function StatRow({
  label,
  value,
  hint,
  onPress,
  testID,
}: {
  label: string;
  value: string;
  hint?: string | null;
  onPress?: () => void;
  testID?: string;
}) {
  const content = (
    <View style={styles.statRow} testID={testID}>
      <Text style={styles.statRowLabel} numberOfLines={2}>
        {label}
      </Text>
      <View style={styles.statRowValueWrap}>
        <Text style={styles.statRowValue} numberOfLines={1}>
          {value}
        </Text>
        {hint ? (
          <Text style={styles.statRowHint} numberOfLines={1}>
            {hint}
          </Text>
        ) : null}
      </View>
    </View>
  );

  return onPress ? <Pressable onPress={onPress}>{content}</Pressable> : content;
}

export function EmptyHint({ text }: { text: string }) {
  return <Text style={styles.emptyHint}>{text}</Text>;
}

export const statColors = {
  // Diverging around a neutral par: green under, warm over. Gold stays reserved for merits
  // and trophies so the two scales never compete.
  eagle: appColors.background.interactive,
  birdie: appColors.accent.primary,
  par: appColors.text.muted,
  bogey: appColors.chart.salmon,
  doublePlus: appColors.status.errorText,
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: appColors.background.panel,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    borderRadius: 14,
    paddingHorizontal: 14,
    paddingVertical: 12,
    gap: 10,
  },
  cardTitle: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: appColors.text.muted,
  },
  tileRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  tile: {
    flexGrow: 1,
    flexBasis: '46%',
    minWidth: 140,
    backgroundColor: appColors.background.panel,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    borderRadius: 14,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  tileValue: {
    fontSize: 22,
    fontWeight: '800',
    color: appColors.text.heading,
  },
  tileValueGold: {
    color: appColors.accent.gold,
  },
  tileSuffix: {
    fontSize: 12,
    fontWeight: '600',
    color: appColors.text.muted,
  },
  tileLabel: {
    fontSize: 11,
    color: appColors.text.muted,
    marginTop: 1,
  },
  tileDelta: {
    fontSize: 11,
    fontWeight: '600',
    color: appColors.accent.primary,
    marginTop: 2,
  },
  tileDeltaBad: {
    color: appColors.status.danger,
  },
  tileDeltaNeutral: {
    color: appColors.text.secondary,
  },
  barRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 3,
  },
  barLabel: {
    width: 66,
    fontSize: 12,
    fontWeight: '600',
    color: appColors.text.secondary,
  },
  barTrack: {
    flex: 1,
    height: 12,
    justifyContent: 'center',
  },
  barFill: {
    height: 12,
    borderTopLeftRadius: 3,
    borderBottomLeftRadius: 3,
    borderTopRightRadius: 6,
    borderBottomRightRadius: 6,
  },
  barValue: {
    // Wide enough for the longest row on the narrowest phone ("156 · 39.3%"); the bar takes
    // whatever is left, so a truncated count is never the thing that gives way.
    width: 92,
    textAlign: 'right',
    fontSize: 12,
    color: appColors.text.secondary,
  },
  stackedRow: {
    paddingVertical: 4,
    gap: 3,
  },
  stackedHeader: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 10,
  },
  stackedLabel: {
    flex: 1,
    minWidth: 0,
    fontSize: 12.5,
    fontWeight: '600',
    color: appColors.text.secondary,
  },
  stackedValue: {
    fontSize: 12,
    color: appColors.text.muted,
  },
  statRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    paddingVertical: 4,
  },
  statRowLabel: {
    flex: 1,
    fontSize: 12.5,
    color: appColors.text.secondary,
  },
  statRowValueWrap: {
    alignItems: 'flex-end',
  },
  statRowValue: {
    fontSize: 12.5,
    fontWeight: '700',
    color: appColors.text.heading,
  },
  statRowHint: {
    fontSize: 11,
    color: appColors.text.muted,
  },
  emptyHint: {
    fontSize: 12.5,
    color: appColors.text.muted,
  },
});
